import { HttpErrorResponse } from '@angular/common/http';
import { ICompanyDTO, ICompanyNameIdLegalFormDTO } from '@modeso/types__aumico-lib-companies-be';
import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import Debug from 'debug';
import * as fromCompaniesActions from '../actions/companies.actions';

const debug = Debug('aumico:aumico-lib-companies-fe:AumicoCompaniesReducer');

export const companiesFeatureKey = 'aumicoCompaniesMicroservice';

export const selectFeature = createFeatureSelector<FeatureState>(companiesFeatureKey);

export interface AppState {
    aumicoCompaniesMicroservice: FeatureState;
}

export interface FeatureState extends EntityState<ICompanyDTO> {
    selectedCompanyById: ICompanyDTO;
    response: ICompanyDTO;
    error: HttpErrorResponse;
    companyCreationResponse?: {
        company: ICompanyDTO;
    };
    companiesNameIdLegalForm: ICompanyNameIdLegalFormDTO[];
}

export function selectCompanyId(a: any): string {
    return a._id;
}

export const adapter: EntityAdapter<ICompanyDTO> = createEntityAdapter<ICompanyDTO>({
    selectId: selectCompanyId,
});

export const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors();

export const selectEntity = createSelector(selectEntities, (entities, props) => entities[props.id]);

export const getselectedCompanyById = (state: FeatureState) => state.selectedCompanyById;

export const getCompaniesNameIdLegalForm = (state: FeatureState) => state.companiesNameIdLegalForm;

export const selectCompaniesEntities = createSelector(selectFeature, selectEntities);

export const selectAllCompanies = createSelector(selectFeature, selectAll);

export const selectCompanyById = createSelector(selectFeature, selectEntity);

export const selectCompanyCreationResponse = createSelector(selectFeature, (state) => state.companyCreationResponse);

export const selectCompaniesNameIdLegalForm = createSelector(selectFeature, getCompaniesNameIdLegalForm);

export const selectSelectedCompanyById = createSelector(selectFeature, getselectedCompanyById);

export const initialState: FeatureState = adapter.getInitialState({
    selectedCompanyById: null,
    response: null,
    error: null,
    companiesNameIdLegalForm: [],
});

export const selectCompaniesState = createSelector(selectFeature, (state: FeatureState) => state.selectedCompanyById);

export const selectFeatureContent = createSelector(selectFeature, (state: FeatureState) => state);

export const selectCompaniesError = createSelector(selectFeature, (state: FeatureState) => state.error);

export const selectCompaniesResponse = createSelector(selectFeature, (state: FeatureState) => state.response);

export const isCompanyUrlIdValid = (id: string) =>
    createSelector(selectFeature, (state: FeatureState) => {
        const foundCompany = state.entities[id];
        return foundCompany != null;
    });

export const companiesReducer = createReducer(
    initialState,
    on(fromCompaniesActions.createCompany, (state) => ({
        ...state,
        companyCreationResponse: undefined,
        error: undefined,
    })),
    on(fromCompaniesActions.onCreateCompanySuccessfully, (state, action) => {
        return adapter.addOne(action.payload.company, {
            ...state,
            selectedCompanyById: undefined,
            error: undefined,
            companyCreationResponse: {
                company: action.payload.company,
            },
        });
    }),
    on(fromCompaniesActions.onCreateCompanyFailed, (state, action) => ({
        ...state,
        selectedCompanyById: undefined,
        error: action.payload,
    })),

    on(fromCompaniesActions.updateCompany, (state) => ({ ...state, error: undefined, companyCreationResponse: null })),
    on(fromCompaniesActions.onUpdateCompanySuccessfully, (state, action) => {
        return adapter.updateOne(
            { id: action.payload._id, changes: action.payload },
            { ...state, selectedCompanyById: action.payload, error: undefined },
        );
    }),
    on(fromCompaniesActions.onUpdateCompanyFailed, (state, action) => ({
        ...state,
        selectedCompanyById: undefined,
        error: action.payload,
    })),

    // **Handle Bulk Update Companies Actions**
    on(fromCompaniesActions.bulkUpdateCompanies, (state) => ({ ...state, error: undefined })),
    on(fromCompaniesActions.onBulkUpdateCompaniesSuccessfully, (state, action) => {
        // Create update objects for each company
        const updates = action.payload.map(company => ({
            id: company._id,
            changes: company
        }));
        return adapter.updateMany(updates, { ...state, error: undefined });
    }),
    on(fromCompaniesActions.onBulkUpdateCompaniesFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),
    on(fromCompaniesActions.changeStatus, (state) => ({ ...state, error: undefined })),
    on(fromCompaniesActions.onChangeStatusSuccessfully, (state, action) => {
        return adapter.updateOne(
            { id: action.payload._id, changes: action.payload },
            { ...state, selectedCompanyById: action.payload, error: undefined },
        );
    }),
    on(fromCompaniesActions.onChangeStatusFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    // **Handle Delete Company Actions**
    on(fromCompaniesActions.deleteCompany, (state) => ({
        ...state,
        companyCreationResponse: null,
        error: undefined,
    })),
    on(fromCompaniesActions.onDeleteCompanySuccessfully, (state, action) => {
        // Remove the company using adapter.removeOne
        const updatedState = adapter.removeOne(action.payload._id, { ...state, error: undefined });

        // If the deleted company was the selected one, clear the selection
        if (state.selectedCompanyById && state.selectedCompanyById._id === action.payload._id) {
            updatedState.selectedCompanyById = null;
        }

        return updatedState;
    }),
    on(fromCompaniesActions.onDeleteCompanyFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromCompaniesActions.getCompanies, (state) => ({ ...state, error: undefined, companyCreationResponse: null })),
    on(fromCompaniesActions.onGettingCompaniesSuccessfully, (state, action) => {
        return adapter.setAll(action.payload, { ...state, selectedCompanyById: undefined, error: undefined });
    }),
    on(fromCompaniesActions.onGettingCompaniesFailed, (state, action) => ({
        ...state,
        selectedCompanyById: undefined,
        error: action.payload,
    })),

    on(fromCompaniesActions.getCompaniesNameIdLegalForm, (state) => ({ ...state, error: undefined })),
    on(fromCompaniesActions.onGettingCompaniesNameIdLegalFormSuccessfully, (state, action) => ({
        ...state,
        error: undefined,
        companiesNameIdLegalForm: action.payload,
    })),
    on(fromCompaniesActions.onGettingCompaniesNameIdLegalFormFailed, (state, action) => ({
        ...state,
        companiesNameIdLegalForm: [],
        error: action.payload,
    })),

    on(fromCompaniesActions.getCompanyById, (state) => ({
        ...state,
        activeCompany: null,
        error: undefined,
        companyCreationResponse: null,
    })),
    on(fromCompaniesActions.onGettingCompanyByIdSuccessfully, (state, action) =>
        adapter.upsertOne(action.payload, {
            ...state,
            selectedCompanyById: action.payload,
            error: undefined,
        }),
    ),
    on(fromCompaniesActions.onGettingCompanyByIdFailed, (state, action) => ({
        ...state,
        selectedCompanyById: null,
        error: action.payload,
    })),

    on(fromCompaniesActions.resetCompanies, (state) => {
        return adapter.removeAll({
            ...state,
            selectedCompanyById: null,
            companyCreationResponse: null,
            error: undefined,
        });
    }),
    on(fromCompaniesActions.resetErrors, (state) => {
        return {
            ...state,
            error: undefined,
        };
    }),
);

export function reducer(state: FeatureState | undefined, action: Action) {
    return companiesReducer(state, action);
}
